import { useEffect, type RefObject } from 'react';

/**
 * Keys that can move the page. Everything else — Tab, typing, Enter — is
 * someone working the form, not someone scrolling away from it.
 */
const SCROLL_KEYS = new Set([
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
  ' ',
  'Spacebar',
]);

const SPACE_KEYS = new Set([' ', 'Spacebar']);

/** Roles whose element Space activates, the way a native button does. */
const ACTIVATED_BY_SPACE_ROLES = new Set([
  'button',
  'checkbox',
  'radio',
  'switch',
  'tab',
  'option',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
]);

/** In a text field these keys move the caret, not the viewport. */
function isTextEntry(el: HTMLElement): boolean {
  return (
    el.isContentEditable ||
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT'
  );
}

/**
 * Space presses a control instead of paging the document. The arrow and page
 * keys are NOT swallowed this way — they still scroll from a focused button.
 */
function isActivatedBySpace(el: HTMLElement): boolean {
  if (el.tagName === 'BUTTON' || el.tagName === 'SUMMARY') return true;
  const role = el.getAttribute('role');
  return role ? ACTIVATED_BY_SPACE_ROLES.has(role) : false;
}

/** Did this key press actually move the viewport, or work a control? */
function scrollsThePage(event: KeyboardEvent): boolean {
  if (!SCROLL_KEYS.has(event.key)) return false;
  const el = event.target as HTMLElement | null;
  // Fired against the document/window rather than a control: nothing to swallow.
  if (!el || typeof el.tagName !== 'string') return true;
  if (isTextEntry(el)) return false;
  if (SPACE_KEYS.has(event.key) && isActivatedBySpace(el)) return false;
  return true;
}

/**
 * Keep the bottom of `ref` in view while `active`, following it as it grows.
 *
 * For content that mounts asynchronously and then resizes — an embedded payment
 * iframe, say — a single `scrollIntoView` at reveal time lands short: the
 * element grows after the scroll, and its last row (the submit button) drops
 * back below the fold. So follow the growth via `ResizeObserver`.
 *
 * Following stops the moment the reader scrolls the page themselves, so we
 * never fight them. That takeover is deliberately NOT "any input": cancelling
 * on every keydown would opt out the keyboard user who Tabs toward the form —
 * exactly the person the follow is for. Only a wheel, a touch drag, or a key
 * press that actually moved the viewport counts, which is why the key check
 * reads the target too: Space types in a field and presses a button, and only
 * pages the document when neither is focused.
 */
export function useFollowIntoView(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    // jsdom has no layout, so it ships no scrollIntoView.
    if (typeof node?.scrollIntoView !== 'function') return;

    let following = true;
    const reveal = () => {
      if (following) node.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };
    const stopFollowing = () => {
      following = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (scrollsThePage(event)) stopFollowing();
    };

    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(reveal) : null;
    observer?.observe(node);
    reveal();
    window.addEventListener('wheel', stopFollowing, { passive: true });
    window.addEventListener('touchmove', stopFollowing, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    return () => {
      stopFollowing();
      observer?.disconnect();
      window.removeEventListener('wheel', stopFollowing);
      window.removeEventListener('touchmove', stopFollowing);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, active]);
}
